import React, { useRef, useState, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

function canUseMagneticHover() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;

    return window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)').matches;
}

/**
 * MagneticButton — Subtly pulls the element toward the cursor on hover.
 * Creates a "magnetic" attraction effect for CTA buttons.
 */
export default function MagneticButton({ children, strength = 0.12, className = '', style = {}, ...rest }) {
    const ref = useRef(null);
    const prefersReducedMotion = useReducedMotion();
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const handleMouseMove = useCallback((e) => {
        if (prefersReducedMotion || !canUseMagneticHover()) return;

        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        setPosition({ x: x * strength, y: y * strength });
    }, [prefersReducedMotion, strength]);

    const handleMouseLeave = useCallback(() => {
        setPosition({ x: 0, y: 0 });
    }, []);

    return (
        <motion.div
            ref={ref}
            className={className}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            animate={{ x: position.x, y: position.y }}
            transition={{ type: 'spring', stiffness: 150, damping: 20, mass: 0.7 }}
            style={{ display: 'inline-block', ...style }}
            data-magnetic-cta="true"
            {...rest}
        >
            {children}
        </motion.div>
    );
}
